import { type FC, useState, useDeferredValue } from 'react';
import { Link } from 'react-router-dom';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faChevronDown,
  faChevronRight,
  faSearch,
  faBars,
  faXmark,
} from '@fortawesome/free-solid-svg-icons';
import { getNavTree, searchDocs } from '../../docs';
import type { DocNavNode } from '../../docs/types';

interface PublicDocsSidebarProps {
  activeDocId?: string;
}

const NavLink: FC<{ node: DocNavNode; activeDocId?: string; depth?: number }> = ({
  node,
  activeDocId,
  depth = 0,
}) => {
  const [expanded, setExpanded] = useState(
    node.type === 'section' ||
    (node.type === 'category' && isActiveInSubtree(node, activeDocId))
  );

  if (node.type === 'page' && node.docId) {
    const isActive = node.docId === activeDocId;
    return (
      <Link
        to={`/docs/${node.docId}`}
        className={`block py-1.5 pr-3 text-[13px] rounded transition-colors ${isActive
          ? 'text-[#9d4edd] bg-[#9d4edd]/10 font-medium'
          : 'text-gray-500 hover:text-gray-900'
          }`}
        style={{ paddingLeft: `${depth * 16 + 12}px` }}
      >
        {node.title}
      </Link>
    );
  }

  const isSectionOrCategory = node.type === 'section' || node.type === 'category';

  return (
    <div>
      <button
        onClick={() => setExpanded((v) => !v)}
        className={`flex items-center justify-between w-full py-1.5 pr-3 text-left transition-colors ${node.type === 'section'
          ? 'text-[11px] font-semibold uppercase tracking-wider text-gray-400 mt-5 first:mt-0 mb-1'
          : 'text-[13px] font-medium text-gray-700 hover:text-gray-900'
          }`}
        style={{ paddingLeft: `${depth * 16 + 12}px` }}
      >
        <span>{node.title}</span>
        {isSectionOrCategory && node.children && node.children.length > 0 && (
          <FontAwesomeIcon
            icon={expanded ? faChevronDown : faChevronRight}
            className="text-[8px] text-gray-400"
          />
        )}
      </button>
      {expanded && node.children && (
        <div>
          {node.children.map((child) => (
            <NavLink
              key={child.id}
              node={child}
              activeDocId={activeDocId}
              depth={depth + 1}
            />
          ))}
        </div>
      )}
    </div>
  );
};

function isActiveInSubtree(node: DocNavNode, activeDocId?: string): boolean {
  if (!activeDocId) return false;
  if (node.docId === activeDocId) return true;
  return node.children?.some((child) => isActiveInSubtree(child, activeDocId)) ?? false;
}

const PublicDocsSidebar: FC<PublicDocsSidebarProps> = ({ activeDocId }) => {
  const [searchQuery, setSearchQuery] = useState('');
  const deferredQuery = useDeferredValue(searchQuery);
  const [mobileOpen, setMobileOpen] = useState(false);
  const tree = getNavTree();
  const searchResults = deferredQuery.length >= 2 ? searchDocs(deferredQuery) : [];

  const sidebarContent = (
    <div className="h-full flex flex-col">
      {/* Search */}
      <div className="p-3 border-b border-gray-200">
        <div className="relative">
          <FontAwesomeIcon
            icon={faSearch}
            className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400 text-xs"
          />
          <input
            type="text"
            placeholder="Search docs..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className="w-full pl-9 pr-3 py-2 bg-gray-50 border border-gray-200 rounded-lg text-sm text-gray-900 placeholder:text-gray-400 focus:outline-none focus:ring-2 focus:ring-[#9d4edd]/30 focus:border-[#9d4edd]/50"
          />
        </div>
      </div>

      {/* Navigation or search results */}
      <nav className="flex-1 overflow-y-auto py-3">
        {searchResults.length > 0 ? (
          <div className="px-3 space-y-1">
            <p className="text-[11px] text-gray-400 font-medium uppercase tracking-wider mb-2 px-3">
              {searchResults.length} result{searchResults.length !== 1 ? 's' : ''}
            </p>
            {searchResults.map((result) => (
              <Link
                key={result.id}
                to={`/docs/${result.id}`}
                onClick={() => {
                  setSearchQuery('');
                  setMobileOpen(false);
                }}
                className={`block px-3 py-2 rounded-lg text-sm transition-colors ${result.id === activeDocId
                  ? 'text-[#9d4edd] bg-[#9d4edd]/10'
                  : 'text-gray-700 hover:bg-gray-50'
                  }`}
              >
                <div className="font-medium">{result.title}</div>
                {result.snippet && (
                  <div className="text-[11px] text-gray-400 mt-0.5 line-clamp-2">
                    {result.snippet}
                  </div>
                )}
              </Link>
            ))}
          </div>
        ) : searchQuery.length >= 2 ? (
          <p className="px-6 py-4 text-sm text-gray-400">No results found.</p>
        ) : (
          tree.map((section) => (
            <NavLink key={section.id} node={section} activeDocId={activeDocId} />
          ))
        )}
      </nav>
    </div>
  );

  return (
    <>
      {/* Mobile toggle */}
      <button
        onClick={() => setMobileOpen((v) => !v)}
        className="lg:hidden fixed top-20 left-4 z-40 p-2 bg-white border border-gray-200 rounded-lg shadow-sm text-gray-600 hover:text-gray-900"
        aria-label="Toggle sidebar"
      >
        <FontAwesomeIcon icon={mobileOpen ? faXmark : faBars} />
      </button>

      {/* Mobile overlay */}
      {mobileOpen && (
        <div
          className="lg:hidden fixed inset-0 bg-black/30 z-30"
          onClick={() => setMobileOpen(false)}
        />
      )}

      {/* Sidebar */}
      <aside
        className={`fixed top-16 left-0 h-[calc(100vh-4rem)] w-72 bg-white border-r border-gray-200 z-30 transition-transform lg:translate-x-0 ${mobileOpen ? 'translate-x-0' : '-translate-x-full'
          }`}
      >
        {sidebarContent}
      </aside>
    </>
  );
};

export default PublicDocsSidebar;
